import { useEffect, useState } from "react";
import * as LucideIcons from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { configService } from "@/services/config";
import { useAuth } from "@/hooks/useAuth";

export const ONBOARDING_CONFIG = 'onboarding_config';
export const ONBOARDING_COMPLETED_KEY = 'onboarding_completed';
export const ONBOARDING_DATA_KEY = 'onboarding_data';

export class OnboardingConfigData {
  profile: boolean = false;
  missionhub: boolean = false;
  knowledge_base: boolean = false;
  calendar: boolean = false;
  ai_chat: boolean = false;
  corporate_event: boolean = false;
  community_event: boolean = false;
}

// Query key for onboarding config
export const onboardingConfigKey = ['onboarding-config'];

// Custom hook to manage onboarding config
export const useOnboardingConfig = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);

  // Check if onboarding was previously completed
  const isOnboardingCompleted = localStorage.getItem(ONBOARDING_COMPLETED_KEY) === "true";

  // Update config service token when session changes
  useEffect(() => {
    if (session?.access_token) {
      configService.updateToken(session.access_token);
    }
  }, [session?.access_token]);

  // Query to fetch onboarding config
  const { data: configData } = useQuery({
    queryKey: onboardingConfigKey,
    queryFn: async () => {
      // If onboarding was completed, use local storage
      if (isOnboardingCompleted) {
        const localData = localStorage.getItem(ONBOARDING_DATA_KEY);
        return localData ? JSON.parse(localData) : new OnboardingConfigData();
      }

      const response = await configService.getConfig(ONBOARDING_CONFIG);
      const config = response.data || new OnboardingConfigData();

      // Check if all steps are completed
      const isComplete = Object.values(config).every(value => value === true);
      if (isComplete) {
        localStorage.setItem(ONBOARDING_COMPLETED_KEY, "true");
        localStorage.setItem(ONBOARDING_DATA_KEY, JSON.stringify(config));
      }
      setIsLoading(false);
      return config;
    },
    enabled: !!session?.access_token && !isOnboardingCompleted
  });

  // Mutation to update config
  const { mutate: updateConfig } = useMutation({
    mutationFn: async (newConfig: Partial<OnboardingConfigData>) => {
      const currentConfig = configData || new OnboardingConfigData();
      const updatedConfig = { ...currentConfig, ...newConfig };

      // If onboarding was completed, only update local storage
      if (isOnboardingCompleted) {
        localStorage.setItem(ONBOARDING_DATA_KEY, JSON.stringify(updatedConfig));
        return updatedConfig;
      }

      const response = await configService.setConfig(ONBOARDING_CONFIG, {
        value: updatedConfig,
        description: 'Onboarding progress configuration',
        isPublic: false
      });

      // Check if all steps are now completed
      const isComplete = Object.values(updatedConfig).every(value => value === true);
      if (isComplete) {
        localStorage.setItem(ONBOARDING_COMPLETED_KEY, "true");
        localStorage.setItem(ONBOARDING_DATA_KEY, JSON.stringify(updatedConfig));
      }
      return response;
    },
    onSuccess: (newConfig) => {
      // Update cache with new config
      queryClient.setQueryData(onboardingConfigKey, newConfig);
    },
    onError: (error) => {
      console.error('Failed to update onboarding config:', error);
    }
  });

  return {
    config: configData || new OnboardingConfigData(),
    updateConfig,
    isLoading,
    isOnboardingCompleted
  };
};

export interface OnboardingStep {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  completed: boolean;
  route: string;
  description: string;
  color: string;
}

export interface OnboardingStepsProps {
  steps: OnboardingStep[];
  className?: string;
  overlayMode?: boolean;
  onClose?: () => void;
}

// Mapping between OnboardingConfigData keys and step IDs
export const configToStepMap: Record<string, keyof OnboardingConfigData> = {
  'profile': 'profile',
  'mission-hub': 'missionhub',
  'knowledge-graph': 'knowledge_base',
  'calendar': 'calendar',
  'ai-assistant': 'ai_chat',
  'corporate-event': 'corporate_event',
  'community-event': 'community_event'
};

export function OnboardingSteps({ 
  className,
  overlayMode = false,
  onClose
}: Omit<OnboardingStepsProps, 'steps'>) {
  const navigate = useNavigate();
  const { config, isOnboardingCompleted } = useOnboardingConfig();

  // Map config to steps
  const steps = defaultOnboardingSteps.map(step => {
    let completed = false;
    switch (step.id) {
      case 'profile':
        completed = config?.profile || false;
        break;
      case 'mission-hub':
        completed = config?.missionhub || false;
        break;
      case 'knowledge-graph':
        completed = config?.knowledge_base || false;
        break;
      case 'calendar':
        completed = config?.calendar || false;
        break;
      case 'ai-assistant':
        completed = config?.ai_chat || false;
        break;
      case 'corporate-event':
        completed = config?.corporate_event || false;
        break;
      case 'community-event':
        completed = config?.community_event || false;
        break;
    }
    return { ...step, completed };
  });

  const handleStepClick = (stepId: string, route: string) => {
    navigate(route);
  };

  const completedStepsCount = steps.filter(step => step.completed).length;
  const progressPercentage = (completedStepsCount / steps.length) * 100;

  return (
    <div className={cn(
      overlayMode ? "fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" : "relative",
      className
    )}>
      <div className={cn(
        "bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-4",
        "border border-primary/20",
        overlayMode ? "max-w-md w-full max-h-[80vh] overflow-y-auto" : "w-full"
      )}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Onboarding Guide</h3>
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <span className="mr-2">{completedStepsCount} of {steps.length} completed</span>
              <span className="text-primary font-medium">{Math.round(progressPercentage)}%</span>
            </div>
          </div>
          {overlayMode && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 h-8 w-8 rounded-full"
            >
              <LucideIcons.X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        <Progress value={progressPercentage} className="h-1.5 mb-3" />
        
        <div className="space-y-2">
          {steps.map((step) => (
            <div 
              key={step.id}
              className="rounded-md overflow-hidden border border-gray-100 shadow-sm transition-all duration-200"
              style={{
                borderLeftWidth: '3px',
                borderLeftColor: step.color
              }}
            >
              <div 
                onClick={() => handleStepClick(step.id, step.route)}
                className={cn(
                  "flex flex-col p-2 cursor-pointer transition-colors",
                  step.completed 
                    ? "bg-gradient-to-r from-gray-50 to-gray-100" 
                    : "bg-white hover:bg-gray-50"
                )}
              >
                <div className="flex items-center">
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center mr-2 transition-all duration-300",
                    step.completed 
                      ? "bg-white border-2 border-green-500" 
                      : "bg-white border-2",
                    !step.completed && `border-${step.color}-500`
                  )}>
                    {step.completed ? (
                      <LucideIcons.CheckCircle2 className="w-3 h-3 text-green-500" />
                    ) : (
                      <step.icon className={cn(
                        "w-3 h-3",
                        `text-${step.color}-500`
                      )} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className={cn(
                      "text-xs font-medium",
                      step.completed ? "text-green-600 line-through" : `text-${step.color}-600`
                    )}>
                      {step.title}
                    </h4>
                    {!step.completed && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {step.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center ml-2">
                    {step.completed && (
                      <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full text-[10px]">
                        Completed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const defaultOnboardingSteps: OnboardingStep[] = [
  {
    id: "profile",
    title: "Complete your profile",
    description: "Add your organization details, mission, and impact areas",
    icon: LucideIcons.Building2,
    completed: false,
    route: "/dashboard/profile",
    color: "blue"
  },
  {
    id: "mission-hub",
    title: "Set up Mission Hub",
    description: "Configure your mission hub to showcase your events, offerings and enable bookings using one link.",
    icon: LucideIcons.Globe,
    completed: false,
    route: "/dashboard/mission-hub/setup",
    color: "purple"
  },
  {
    id: "knowledge-graph",
    title: "Build Knowledge Base",
    description: "Upload documents to create your organization's knowledge base",
    icon: LucideIcons.Network,
    completed: false,
    route: "/dashboard/knowledge/graph",
    color: "indigo"
  },
  {
    id: "ai-assistant",
    title: "Configure AI Assistant",
    description: "Set up your AI chat assistant for automated support",
    icon: LucideIcons.Bot,
    completed: false,
    route: "/dashboard/chatbot/setup",
    color: "cyan"
  },
  {
    id: "calendar",
    title: "Link your Calendar",
    description: "Connect your Google Calendar to enable auto scheduling to your customers",
    icon: LucideIcons.Calendar,
    completed: false,
    route: "/dashboard/settings",
    color: "#06b6d4"
  },
  {
    id: "corporate-event",
    title: "Create Corporate Event",
    description: "Create your first corporate volunteering opportunity",
    icon: LucideIcons.Building,
    completed: false,
    route: "/dashboard/campaigns",
    color: "amber"
  },
  {
    id: "community-event",
    title: "Create Community Event",
    description: "Create your first community volunteering event",
    icon: LucideIcons.Users,
    completed: false,
    route: "/dashboard/community-events/create",
    color: "#14b8a6"
  }
];
